import {call, put, select, takeEvery} from 'redux-saga/effects';
import {toast} from 'react-toastify';
import {
  cancelOrderDelivery,
  deleteDriverRatingApi,
  getDeliveryTrackingApi,
  getDispatchInfoApi,
  getDriverRatingApi,
  getOrderApi,
  rateDriverApi,
  updateDriverRatingApi
} from 'helpers/services/api/ordersV2';
import {getBadOrdersByCoreOrderApi} from 'helpers/services/api/largeDelivery';
import {
  CANCEL_DELIVERY_ORDER,
  CHANGE_STATUS,
  CHANGE_STATUS_SUCCESS,
  DELETE_DRIVER_RATING,
  DELETE_DRIVER_RATING_ERROR,
  DELETE_DRIVER_RATING_SUCCESS,
  GET_BAD_ORDERS_BY_CORE,
  GET_DELIVERY_STATUS,
  GET_DELIVERY_STATUS_SUCCESS,
  GET_DISPATCH_INFO,
  GET_DISPATCH_INFO_SUCCESS,
  GET_DRIVER_RATING,
  GET_DRIVER_RATING_ERROR,
  GET_DRIVER_RATING_SUCCESS,
  GET_ORDER,
  GET_ORDER_SUCCESS,
  SET_ORDER_LOADING,
  OPEN_CANCEL_DELIVERY_ORDER_MODAL,
  OPEN_CANCEL_DELIVERY_ORDER_RESULT_MODAL,
  RATE_DRIVER,
  RATE_DRIVER_ERROR,
  RATE_DRIVER_SUCCESS,
  SET_BAD_ORDERS_BY_CORE,
  SET_DELIVERY_STATUS_LOADING,
  UPDATE_DRIVER_RATING,
  UPDATE_DRIVER_RATING_ERROR,
  UPDATE_DRIVER_RATING_SUCCESS
} from 'store/ordersV2/orderDetails/action';
import {changeStatusActiveOrder} from 'store/global/ordersV2/saga';
import {changeStatusHistory} from 'store/ordersV2/orderList/saga';
import {SET_LOADING_CANCELLATION_V2} from 'store/global/modals/action';
import {hasLargeDeliveryAccess} from 'helpers/constants/restrictions';
import {
  getBaDCoreOrderId,
  getBaDCoreOrderType,
  isLargeDeliveryOrder
} from 'helpers/functionUtils/ordersUtilsV2';

export function* getOrder({payload}) {
  const orderId = payload?.id ?? payload;

  try {
    const activeLocation = yield select((state) => state.user.activeLocation);
    const {
      Timezone,
      PickupReadyEnabled,
      AllowedServiceFeatures: serviceFeatures
    } = activeLocation;
    const {data} = yield getOrderApi(orderId);

    // A background refresh can land after the merchant opened another order;
    // storing it then would show this order under the other one's URL.
    if (payload?.silent) {
      const openOrderId = yield select(
        (state) => state.orderDetailsV2Reducer.order?.id
      );
      if (openOrderId !== orderId) return;
    }

    if (data) {
      const order = {Timezone, ...data};
      yield put(
        GET_ORDER_SUCCESS({
          order,
          PickupReadyEnabled,
          serviceFeatures
        })
      );
      if (
        hasLargeDeliveryAccess(serviceFeatures, activeLocation) &&
        isLargeDeliveryOrder(order)
      ) {
        yield put(GET_BAD_ORDERS_BY_CORE());
      } else {
        yield put(SET_BAD_ORDERS_BY_CORE([]));
      }
    }
  } catch (error) {
    console.log('SAGA ERROR', error);
    yield put(SET_ORDER_LOADING(false));
  }
}

export function* getDispatchInfo({payload}) {
  try {
    const {data} = yield getDispatchInfoApi(payload);

    if (data) {
      yield put(GET_DISPATCH_INFO_SUCCESS(data));
    }
  } catch (error) {
    console.log('SAGA ERROR', error);
  }
}

export function* changeStatus({payload: {location, ...values}}) {
  try {
    const order = yield select((state) => state.orderDetailsV2Reducer.order);

    yield put(SET_LOADING_CANCELLATION_V2(true));
    let success;

    if (location === 'history') {
      success = yield changeStatusHistory({
        payload: {...values},
        orderId: order?.id
      });
    } else {
      success = yield changeStatusActiveOrder({
        payload: {...values},
        orderId: order?.id
      });
    }

    if (success) {
      yield put(CHANGE_STATUS_SUCCESS(success));
    }
    yield put(SET_LOADING_CANCELLATION_V2(false));
  } catch (error) {
    yield put(SET_LOADING_CANCELLATION_V2(false));
    console.log('SAGA ERROR', error);
  }
}

const mapDeliveryTracking = (tracking) => ({
  status: tracking.status,
  lastUpdateDate: tracking.updated_at,
  dispatchVersion: tracking.version,
  dspOrderId: tracking.provider_order_id,
  courier: {
    name: tracking.courier?.name,
    vehicle: tracking.courier?.vehicle,
    deliveryServiceProvider: tracking.courier?.provider_name
  },
  proofOfDelivery: tracking.proof_of_delivery
    ? {
        photoUrl: tracking.proof_of_delivery.photo_url,
        deliveredAt: tracking.delivered_at
      }
    : null
});

function* getDeliveryStatus({payload}) {
  try {
    const location = yield select((state) => state.user.activeLocation);
    const {data} = yield getDeliveryTrackingApi(payload, location?._id);

    if (data?.data) {
      yield put(GET_DELIVERY_STATUS_SUCCESS(mapDeliveryTracking(data.data)));
    }
    yield put(SET_DELIVERY_STATUS_LOADING(false));
  } catch (error) {
    yield put(SET_DELIVERY_STATUS_LOADING(false));
    console.log('SAGA ERROR', error);
  }
}

function* cancelDeliveryOrder({payload}) {
  try {
    const {status} = yield cancelOrderDelivery(payload.deliveryId, {
      reasonComment: payload.reasonComment
    });

    if (status === 200) {
      yield put(GET_ORDER(payload.orderId));
      yield put(GET_DELIVERY_STATUS(payload.deliveryId));
      yield put(OPEN_CANCEL_DELIVERY_ORDER_MODAL(false));
      yield put(
        OPEN_CANCEL_DELIVERY_ORDER_RESULT_MODAL({
          isOpenModal: true,
          message: 'Driver has been successfully canceled'
        })
      );
    }
  } catch (error) {
    yield put(OPEN_CANCEL_DELIVERY_ORDER_MODAL(false));
    yield put(GET_DELIVERY_STATUS(payload.deliveryId));
    yield put(
      OPEN_CANCEL_DELIVERY_ORDER_RESULT_MODAL({
        isOpenModal: true,
        message: 'Sorry, driver cannot be canceled'
      })
    );
    console.log('SAGA ERROR', error);
  }
}

function* rateDriver({payload}) {
  try {
    const {data} = yield rateDriverApi(payload);

    if (data?.data) {
      yield put(RATE_DRIVER_SUCCESS(data?.data));
      toast.success('Driver rating submitted successfully');
    }
  } catch (error) {
    yield put(RATE_DRIVER_ERROR(error));
    console.log('SAGA ERROR', error);
  }
}

function* getDriverRating({payload}) {
  try {
    const {data} = yield getDriverRatingApi(payload);

    if (data?.data) {
      yield put(GET_DRIVER_RATING_SUCCESS(data?.data));
    } else {
      yield put(GET_DRIVER_RATING_SUCCESS(null));
    }
  } catch (error) {
    if (error?.response?.status === 404) {
      yield put(GET_DRIVER_RATING_SUCCESS(null));
    } else {
      yield put(GET_DRIVER_RATING_ERROR(error));
      console.log('SAGA ERROR', error);
    }
  }
}

function* updateDriverRating({payload: {deliveryId, ...data}}) {
  try {
    const {data: responseData} = yield updateDriverRatingApi(deliveryId, data);

    if (responseData?.data) {
      yield put(UPDATE_DRIVER_RATING_SUCCESS(responseData?.data));
      toast.success('Driver rating updated successfully');
    }
  } catch (error) {
    yield put(UPDATE_DRIVER_RATING_ERROR(error));
    console.log('SAGA ERROR', error);
  }
}

function* deleteDriverRating({payload}) {
  try {
    yield deleteDriverRatingApi(payload);

    yield put(DELETE_DRIVER_RATING_SUCCESS());
    yield put(GET_DRIVER_RATING(payload));
    toast.success('Driver rating removed successfully');
  } catch (error) {
    yield put(DELETE_DRIVER_RATING_ERROR(error));
    console.log('SAGA ERROR', error);
  }
}

function* getBadOrdersByCore() {
  try {
    const order = yield select((state) => state.orderDetailsV2Reducer.order);
    const location = yield select((state) => state.user.activeLocation);
    const coreOrderId = getBaDCoreOrderId(order);

    if (!location?._id || !coreOrderId) {
      yield put(SET_BAD_ORDERS_BY_CORE([]));
      return;
    }

    const {data} = yield call(
      getBadOrdersByCoreOrderApi,
      coreOrderId,
      location._id,
      getBaDCoreOrderType(order)
    );
    const orders = data?.data || data || [];
    yield put(SET_BAD_ORDERS_BY_CORE(Array.isArray(orders) ? orders : []));
  } catch (error) {
    yield put(SET_BAD_ORDERS_BY_CORE([]));
    console.log('SAGA ERROR', error);
  }
}

function* orderDetailsV2Saga() {
  yield takeEvery(GET_ORDER, getOrder);
  yield takeEvery(CHANGE_STATUS, changeStatus);
  yield takeEvery(GET_DISPATCH_INFO, getDispatchInfo);
  yield takeEvery(GET_DELIVERY_STATUS, getDeliveryStatus);
  yield takeEvery(CANCEL_DELIVERY_ORDER, cancelDeliveryOrder);
  yield takeEvery(RATE_DRIVER, rateDriver);
  yield takeEvery(GET_DRIVER_RATING, getDriverRating);
  yield takeEvery(UPDATE_DRIVER_RATING, updateDriverRating);
  yield takeEvery(DELETE_DRIVER_RATING, deleteDriverRating);
  yield takeEvery(GET_BAD_ORDERS_BY_CORE, getBadOrdersByCore);
}

export default orderDetailsV2Saga;
